import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Chip, Button, Checkbox,
  FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Collapse, Stack, Divider, FormGroup,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';
import { getRoles, getPermissions, updateRolePermissions, createRole } from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const formatRoleName = (name) =>
  (name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const groupPermissions = (perms) => {
  const groups = {};
  perms.forEach(p => {
    const [module] = p.codename.split('.');
    const label = module.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    if (!groups[label]) groups[label] = [];
    groups[label].push(p);
  });
  return groups;
};

export default function RoleManagementPage() {
  const { showToast } = useToast();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPerms, setNewPerms] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesData, permsData] = await Promise.all([getRoles(), getPermissions()]);
      setRoles(rolesData);
      setPermissions(permsData);
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEditOpen = (role) => {
    setEditRole(role);
    setEditPerms([...(role.permissions || [])]);
  };

  const handlePermToggle = (codename, setter) => {
    setter(prev => prev.includes(codename) ? prev.filter(p => p !== codename) : [...prev, codename]);
  };

  const handleSavePerms = async () => {
    try {
      setSaving(true);
      await updateRolePermissions(editRole.id, editPerms);
      showToast('Permissions updated', 'success');
      setEditRole(null);
      await fetchData();
    } catch (e) {
      showToast(e.message || 'Failed to update', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await createRole(newName, newDesc, newPerms);
      showToast('Role created', 'success');
      setCreateOpen(false);
      setNewName('');
      setNewDesc('');
      setNewPerms([]);
      await fetchData();
    } catch (e) {
      showToast(e.message || 'Failed to create', 'error');
    } finally {
      setSaving(false);
    }
  };

  const grouped = groupPermissions(permissions);

  const sortedRoles = useMemo(() => {
    const adminFirst = [...roles];
    adminFirst.sort((a, b) => {
      const aAdmin = (a.name || '').toLowerCase().trim() === 'admin';
      const bAdmin = (b.name || '').toLowerCase().trim() === 'admin';
      if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;
      return (a.id || 0) - (b.id || 0);
    });
    return adminFirst;
  }, [roles]);

  const renderPermCheckboxes = (permList, setter) => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {Object.entries(grouped).map(([group, perms]) => (
        <Box key={group}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            {group}
          </Typography>
          <FormGroup row>
            {perms.map(p => (
              <FormControlLabel
                key={p.codename}
                control={
                  <Checkbox
                    size="small"
                    checked={permList.includes(p.codename)}
                    onChange={() => handlePermToggle(p.codename, setter)}
                  />
                }
                label={<Typography variant="body2">{p.name || p.codename}</Typography>}
                sx={{ minWidth: 200, mr: 2 }}
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Stack>
  );

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Role Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ textTransform: 'none' }}>
              New Role
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={0}>
              {sortedRoles.map((r, idx) => {
                const isExpanded = expandedRole === r.id;
                const isAdmin = (r.name || '').toLowerCase().trim() === 'admin';
                const permCount = (r.permissions || []).length;

                return (
                  <Paper
                    key={r.id}
                    variant="outlined"
                    sx={{
                      borderRadius: idx === 0 ? '8px 8px 0 0' : idx === sortedRoles.length - 1 ? '0 0 8px 8px' : 0,
                      borderTop: idx === 0 ? undefined : 'none',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2.5,
                        py: 1.5,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#fafafa' },
                        transition: 'background-color 0.15s',
                      }}
                      onClick={() => setExpandedRole(isExpanded ? null : r.id)}
                    >
                      <IconButton size="small" sx={{ mr: 1 }}>
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>

                      <Box sx={{ flex: '0 0 220px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {formatRoleName(r.name)}
                        </Typography>
                        {r.is_system && (
                          <Chip label="System" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1, minWidth: 0, px: 2 }}
                        noWrap
                        title={r.description || ''}
                      >
                        {r.description || '—'}
                      </Typography>

                      <Chip
                        label={`${permCount} permission${permCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 2, minWidth: 120, justifyContent: 'center', flexShrink: 0 }}
                      />

                      {!isAdmin ? (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleEditOpen(r); }}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 34 }} />
                      )}
                    </Box>

                    <Collapse in={isExpanded} unmountOnExit>
                      <Divider />
                      <Box sx={{ px: 3, py: 2, backgroundColor: '#f9fafb' }}>
                        {permCount > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {(r.permissions || []).map(p => (
                              <Chip
                                key={p}
                                label={p}
                                size="small"
                                sx={{
                                  fontSize: '0.75rem',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No permissions assigned
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>
          )}

          {/* Edit permissions dialog */}
          <Dialog open={!!editRole} onClose={() => !saving && setEditRole(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
              Edit Permissions
              <Typography variant="body2" color="text.secondary">{formatRoleName(editRole?.name)}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              {renderPermCheckboxes(editPerms, setEditPerms)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditRole(null)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleSavePerms} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Create role dialog */}
          <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Create New Role</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField fullWidth label="Role Name" value={newName} onChange={e => setNewName(e.target.value)} size="small" />
                <TextField fullWidth label="Description" value={newDesc} onChange={e => setNewDesc(e.target.value)} size="small" />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Permissions</Typography>
                  {renderPermCheckboxes(newPerms, setNewPerms)}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={saving || !newName.trim()}>
                {saving ? 'Creating...' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </ContentContainer>
      </PageContainer>
    </>
  );
}
